import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Text, Button, SelectInput, TextInput, DateInput, StatusBadge, Icon} from '@/ui-kits';
import {DataTable, InviteHospitalSpecialistModal, EditHospitalSpecialistModal} from '@/components';
import {NA, HospitalSpecialistOnboardingStatus, HospitalSpecialistOnboardingStatusBadgeVariants, HospitalSpecialistOnboardingStatusLabels} from '@/constants';
import {SelectInputitem, DataTableColumn, GetHospitalSpecialistsRequestPayload, InviteHospitalSpecialistRequestPayload, EditHospitalSpecialistRequestPayload, InvitedSpecialist} from '@/interface';
import {useDispatch, useSelector} from 'react-redux';
import {resetHospitalAdminMessage, getHospitalSpecialistsRequest, inviteHospitalSpecialistRequest, editHospitalSpecialistRequest} from '@/services/redux/slices';
import {
  hospitalAdminLoadingSelector,
  hospitalAdminSuccessSelector,
  hospitalAdminFailureSelector,
  hospitalAdminSpecialistsSelector,
  hospitalAdminSpecialistsCurrentPageSelector,
  hospitalAdminSpecialistsTotalPagesSelector,
  hospitalAdminSpecialistsTotalResultsSelector,
  hospitalPocProfileSpecializationsSelector,
  hospitalAdminSpecialistsNoDataSelector,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, SuccessCodes, getSuccessMessage} from '@/utils/getMessages';
import {useToast, useConfimationModal, useDebounceState} from '@/hooks';
import {enumToSelectOptions, formatDate, formatName} from '@/utils/common_functions';
import {LoaderCircle} from 'lucide-react';

const statusOptions = enumToSelectOptions(HospitalSpecialistOnboardingStatus);

export const HospitalPocDashboard: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {showModal} = useConfimationModal();

  // ==========================
  // selectors
  // ==========================
  const hospitalAdminSpecialists = useSelector(hospitalAdminSpecialistsSelector);
  const hospitalAdminSpecialistsCurrentPage = useSelector(hospitalAdminSpecialistsCurrentPageSelector);
  const hospitalAdminSpecialistsTotalPages = useSelector(hospitalAdminSpecialistsTotalPagesSelector);
  const hospitalAdminSpecialistsTotalResults = useSelector(hospitalAdminSpecialistsTotalResultsSelector);
  const hospitalAdminLoading = useSelector(hospitalAdminLoadingSelector);
  const hospitalAdminSuccess = useSelector(hospitalAdminSuccessSelector) as SuccessCodes;
  const hospitalAdminFailure = useSelector(hospitalAdminFailureSelector) as ErrorCodes;
  const hospitalAdminSpecialistsNoData = useSelector(hospitalAdminSpecialistsNoDataSelector);
  const hospitalPocProfileSpecializations = useSelector(hospitalPocProfileSpecializationsSelector);

  // ==========================
  // states
  // ==========================
  const activeHospitalSpecialization = useMemo(() => {
    return hospitalPocProfileSpecializations.filter(specialization => specialization.status);
  }, [hospitalPocProfileSpecializations]);

  const [search, setSearch, immediateSearch] = useDebounceState<string>('');
  const [status, setStatus, immediateStatus] = useDebounceState<SelectInputitem | null>(null);
  const [from, setFrom, immediateFrom] = useDebounceState<Date | null>(null);
  const [to, setTo, immediateTo] = useDebounceState<Date | null>(null);
  const [page, setPage, _] = useDebounceState<number>(1);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isInviteSpecialistModalOpen, setIsInviteSpecialistModalOpen] = useState(false);
  const [isEditSpecialistModalOpen, setIsEditSpecialistModalOpen] = useState(false);
  const [selectedSpecialistId, setSelectedSpecialistId] = useState<number | null>(null);
  const [specialization, setSpecialization, immediateSpecialization] = useDebounceState<SelectInputitem | null>(null);

  // Get selected specialist from Redux state (updates in realtime via WebSocket)
  const selectedSpecialist = useMemo(() => {
    if (!selectedSpecialistId) return null;
    return hospitalAdminSpecialists.find(s => s.id === selectedSpecialistId) || null;
  }, [selectedSpecialistId, hospitalAdminSpecialists]);

  // ==========================
  // columns definition (moved inside component to access state handlers)
  // ==========================
  const columns: DataTableColumn<InvitedSpecialist>[] = useMemo(() => [
    {
      name: 'id',
      align: 'center',
      title: 'Invite Id',
      render: row => row.id,
      width: {minWidth: '150px', maxWidth: '150px'},
    },
    {
      name: 'name',
      align: 'left',
      title: 'SPECIALIST',
      render: row => <Text className="break-words text-wrap">Dr. {formatName(row.name)}</Text>,
      width: {minWidth: '150px', maxWidth: '150px'},
    },
    {
      name: 'email',
      align: 'left',
      title: 'EMAIL',
      render: row => <Text className="break-words text-wrap">{row.email}</Text>,
      width: {minWidth: '200px', maxWidth: '350px'},
    },
    {
      name: 'phone',
      align: 'center',
      title: 'PHONE',
      render: row => <Text className="break-words text-wrap">{row.phone}</Text>,
      width: {minWidth: '200px', maxWidth: '350px'},
    },
    {
      name: 'specialization',
      align: 'left',
      title: 'SPECIALIZATION',
      render: row => <Text className="break-words text-wrap">{row.specialization.label}</Text>,
      width: {minWidth: '250px', maxWidth: '500px'},
    },
    {
      name: 'status',
      align: 'center',
      title: 'STATUS',
      render: row => (
        <StatusBadge
          variant="secondary"
          className="py-1"
          status={HospitalSpecialistOnboardingStatusBadgeVariants[row.onboarding_status.id]}
          children={HospitalSpecialistOnboardingStatusLabels[row.onboarding_status.id]}
        />
      ),
      width: {minWidth: '150px', maxWidth: '250px'},
    },
    {
      name: 'last_login',
      align: 'center',
      title: 'LAST LOGIN',
      render: row => <Text>{row.last_login ? formatDate(row.last_login ?? '', 'dd-MMM-yyyy') : '--'}</Text>,
      width: {minWidth: '150px', maxWidth: '150px'},
    },
    {
      name: 'action',
      align: 'center',
      title: 'ACTION',
      render: row => (
        row.onboarding_status.id === HospitalSpecialistOnboardingStatus.Invited ? (
          <button
            onClick={() => handleEditSpecialist(row)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Edit Specialist"
          >
            <Icon type="edit_input" className="size-5" />
          </button>
        ) : (
          <Text>-</Text>
        )
      ),
      width: {minWidth: '100px', maxWidth: '100px'},
    },
  ], []);

  // ==========================
  // function
  // ==========================
  const handleEditSpecialist = useCallback((specialist: InvitedSpecialist) => {
    setSelectedSpecialistId(specialist.id);
    setIsEditSpecialistModalOpen(true);
  }, []);

  // ==========================
  // function
  // ==========================
  const resetFilter = () => {
    setSearch('');
    setStatus(null);
    setFrom(null);
    setTo(null);
    setPage(1);
    setSpecialization(null);
  };

  const fetchHospitalSpecialists = useCallback(() => {
    const payload: GetHospitalSpecialistsRequestPayload = {
      page,
    };
    if (status) {
      payload.status = status.id;
    }
    if (from) {
      payload.start_date = from.toISOString();
    }
    if (to) {
      payload.end_date = to.toISOString();
    }
    if (search) {
      payload.search_text = search;
    }
    if (specialization) {
      payload.specialization = specialization.id;
    }

    const isFilterActive = Boolean(search || status || specialization || from || to);
    setIsFilterActive(isFilterActive);

    // fetch list
    dispatch(getHospitalSpecialistsRequest(payload));
  }, [page, search, status, specialization, from, to]);

  const addHospitalPoc = (values: InviteHospitalSpecialistRequestPayload) => {
    dispatch(inviteHospitalSpecialistRequest(values));
  };

  const editHospitalSpecialist = (values: EditHospitalSpecialistRequestPayload) => {
    dispatch(editHospitalSpecialistRequest(values));
  };

  // ==========================
  // side effects
  // ==========================

  // use effect for fetching data
  useEffect(() => {
    // const timeout = setTimeout(fetchHospitalSpecialists, 500);
    // return () => clearTimeout(timeout);
    fetchHospitalSpecialists();
  }, [fetchHospitalSpecialists]);

  // toast handler use effect
  useEffect(() => {
    if (hospitalAdminSuccess) {
      if (['S-10094', 'S-10124'].includes(hospitalAdminSuccess)) {
        showToast(getSuccessMessage(hospitalAdminSuccess), 'success');
      }

      if (hospitalAdminSuccess === 'S-10094') {
        // when hospital admin is added successfully, refetch the list
        fetchHospitalSpecialists();
        setIsInviteSpecialistModalOpen(false);
      }

      if (hospitalAdminSuccess === 'S-10124') {
        // when hospital specialist is edited successfully, close modal and reset state
        setIsEditSpecialistModalOpen(false);
        setSelectedSpecialistId(null);
      }
    }
    if (hospitalAdminFailure) {
      if (!['E-10044', 'E-10054'].includes(hospitalAdminFailure)) {
        showToast(getErrorMessage(hospitalAdminFailure), 'failure');
      }
    }

    return () => {
      dispatch(resetHospitalAdminMessage());
    };
  }, [hospitalAdminSuccess, hospitalAdminFailure]);

  if (hospitalAdminLoading && !isFilterActive) {
    return (
      <div className="flex items-center justify-center px-4 sm:px-6 py-4 sm:py-6">
        <span className="text-ui_grey">Loading...</span>
        <LoaderCircle className="size-4 animate-spin text-ui_grey ml-2" />
      </div>
    );
  }

  if (!hospitalAdminLoading && hospitalAdminSpecialistsNoData) {
    return (
      <div className="p-4 flex-col flex gap-2">
        <div className="bg-white shadow-md border border-ui_input_border py-8 sm:py-12 sm:px-4 px-2 flex flex-col gap-4 justify-center items-center rounded-3xl">
          <Text variant="heading" className="text-ui_blue text-center">
            No specialists added yet!
          </Text>

          <Button leftIcon="plus_white" iconClassName="size-4" title="Add Specialist" variant="primary" onClick={() => setIsInviteSpecialistModalOpen(true)} />
        </div>

        {isInviteSpecialistModalOpen && (
          <InviteHospitalSpecialistModal onSave={addHospitalPoc} open={isInviteSpecialistModalOpen} onClose={() => setIsInviteSpecialistModalOpen(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="sm:p-4 md:p-6 lg:p-8 p-2 flex-col flex gap-4 md:gap-6 lg:gap-8">
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-2 flex-col">
          <Text variant="free" className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-ui_blue !font-FigtreeSemiBold md:!font-FigtreeBold">
            Specialists List
          </Text>
          <Text variant="body" className="text-ui_blue !font-FigtreeMedium">
            {`${hospitalAdminSpecialistsTotalResults} total specialists`}
          </Text>
        </div>
        <Button
          leftIcon="plus_white"
          textClassName="text-nowrap"
          iconClassName="size-4"
          title="Add Specialist"
          variant="primary"
          onClick={() => setIsInviteSpecialistModalOpen(true)}
        />
      </div>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={immediateSearch}
          onChangeText={setSearch}
          placeholder="Search by Name, Email or ID"
          leftIcon="search_blue"
          wrapperClassName="pl-2 border"
          inputClassName="min-w-[235px]"
          className="grow md:min-w-[240px] col-span-full md:max-w-[360px]"
        />
        <SelectInput
          value={immediateStatus?.id || ''}
          options={statusOptions}
          onChange={setStatus}
          placeholder="Status"
          wrapperClassName="border"
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
        />
        <SelectInput
          value={immediateSpecialization?.id || ''}
          options={hospitalPocProfileSpecializations}
          onChange={setSpecialization}
          placeholder="Specialization"
          wrapperClassName="border"
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
        />
        <DateInput
          value={immediateFrom}
          onChangeDate={setFrom}
          placeholder="From"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start text-ui_blue"
          maxValue={immediateTo ?? new Date()}
        />
        <DateInput
          value={immediateTo}
          onChangeDate={setTo}
          placeholder="To"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          textClassName="text-start text-ui_blue"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          minValue={immediateFrom ?? undefined}
          maxValue={new Date()}
        />

        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={hospitalAdminSpecialists}
        totalPages={hospitalAdminSpecialistsTotalPages}
        currentPage={hospitalAdminSpecialistsCurrentPage}
        totalResult={hospitalAdminSpecialistsTotalResults}
        onPageChange={setPage}
        pageSize={5}
        loading={hospitalAdminLoading}
      />

      {isInviteSpecialistModalOpen && (
        <InviteHospitalSpecialistModal onSave={addHospitalPoc} open={isInviteSpecialistModalOpen} onClose={() => setIsInviteSpecialistModalOpen(false)} />
      )}

      {isEditSpecialistModalOpen && selectedSpecialist && (
        <EditHospitalSpecialistModal
          open={isEditSpecialistModalOpen}
          onClose={() => {
            setIsEditSpecialistModalOpen(false);
            setSelectedSpecialistId(null);
          }}
          onSave={editHospitalSpecialist}
          specialist={selectedSpecialist}
        />
      )}
    </div>
  );
};
